import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/auth.service';
import { User, LoginRequest, CompanyInfo } from '../types';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  loading: boolean;
  companies: CompanyInfo[];
  currentCompanyId: number | null;
  currentCompanyName: string;
  switchCompany: (companyId: number) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState<CompanyInfo[]>([]);
  const [currentCompanyId, setCurrentCompanyId] = useState<number | null>(null);

  useEffect(() => {
    const token = authService.getToken();
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUser({
          userId: payload.sub || payload.nameid,
          username: payload.unique_name || payload.name,
          email: payload.email || '',
          roles: payload.role ? (Array.isArray(payload.role) ? payload.role : [payload.role]) : [],
        });

        // Restore company state from localStorage
        const savedCompanies = localStorage.getItem('companies');
        if (savedCompanies) setCompanies(JSON.parse(savedCompanies));

        const savedCompanyId = localStorage.getItem('currentCompanyId');
        if (savedCompanyId) setCurrentCompanyId(Number(savedCompanyId));
      } catch {
        authService.removeToken();
      }
    }
    setLoading(false);
  }, []);

  const login = async (credentials: LoginRequest) => {
    const response = await authService.login(credentials);
    authService.setToken(response.token);
    setUser({
      userId: '',
      username: response.username,
      email: response.email,
      roles: response.roles,
    });

    // Set companies
    const comps = response.companies || [];
    setCompanies(comps);
    localStorage.setItem('companies', JSON.stringify(comps));

    // Set current company — use defaultCompanyId, or first company, or 1
    const compId = response.defaultCompanyId || (comps.length > 0 ? comps[0].id : 1);
    setCurrentCompanyId(compId);
    localStorage.setItem('currentCompanyId', String(compId));
  };

  const logout = () => {
    authService.removeToken();
    localStorage.removeItem('currentCompanyId');
    localStorage.removeItem('companies');
    setUser(null);
    setCompanies([]);
    setCurrentCompanyId(null);
  };

  const switchCompany = (companyId: number) => {
    setCurrentCompanyId(companyId);
    localStorage.setItem('currentCompanyId', String(companyId));
    // Reload to refresh all data for new company
    window.location.reload();
  };

  const currentCompanyName = companies.find(c => c.id === currentCompanyId)?.name || 'ERP System';

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        login,
        logout,
        loading,
        companies,
        currentCompanyId,
        currentCompanyName,
        switchCompany,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
